import { useEffect, useState } from 'react';

type BootstrapDisabled = {
  configured: false;
  message: string;
};

type BootstrapOk = {
  configured: true;
  appId: string;
  redirectUri: string;
  state: string;
  qrconnectUrl: string;
};

type ApiEnvelope<T> = {
  success?: boolean;
  data: T;
  message?: string;
};

declare global {
  interface Window {
    WxLogin?: new (opts: Record<string, unknown>) => void;
  }
}

/** 已登录用户绑定微信：加载开放平台 WxLogin 二维码（mode=bind） */
export function useWeChatBindQr(enabled: boolean, returnUrl?: string | null) {
  const [bootstrap, setBootstrap] = useState<BootstrapDisabled | BootstrapOk | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ mode: 'bind' });
        if (returnUrl?.trim()) {
          params.set('returnUrl', returnUrl.trim());
        }
        const res = await fetch(`/api/wechat/open/bootstrap?${params.toString()}`);
        const json = (await res.json()) as ApiEnvelope<BootstrapDisabled | BootstrapOk>;
        if (!cancelled) {
          setBootstrap(json.data);
          if (!json.data.configured) {
            setLoadError(json.data.message);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : String(e));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, returnUrl]);

  useEffect(() => {
    if (!enabled || !bootstrap || !bootstrap.configured) return;

    const el = document.getElementById('wx_bind_container');
    if (!el) return;

    const script = document.createElement('script');
    script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
    script.async = true;
    script.onload = () => {
      if (!window.WxLogin) return;
      el.innerHTML = '';
      new window.WxLogin({
        self_redirect: false,
        id: 'wx_bind_container',
        appid: bootstrap.appId,
        scope: 'snsapi_login',
        redirect_uri: encodeURIComponent(bootstrap.redirectUri),
        state: bootstrap.state,
        style: 'black',
        href: '',
        fast_login: true,
      });
    };
    script.onerror = () => setLoadError('无法加载微信登录组件');
    document.body.appendChild(script);

    return () => {
      script.remove();
      el.innerHTML = '';
    };
  }, [enabled, bootstrap]);

  return { bootstrap, loadError };
}
